/**
 * The "?" next to a subfield in the record form.
 *
 * CZNA-1339 — it sits next to EVERY subfield and answers "where does this value come
 * from" without leaving the form: the field and subfield, its name and repeatability, the
 * source (an authority type, or a register — ours or aligned to a standard, open or
 * closed) and links to the register, its usage report and the semantic catalog. All of it
 * is derived (`subfieldSource.ts`): the source from the effective form config, ownership
 * and repeatability from the semantic catalog.
 *
 * CZNA-1340 — when NK's methodics team described what the subfield means in its field
 * (`100 $c` ≠ `400 $c`), that description leads the popover, with where the wording comes
 * from. The text is data, not a packaged message: NK edits it in the semantic catalog
 * browser and it arrives with `GET /help-texts` (see `HelpTextProvider`). So the body is
 * built here instead of resolving an i18n id, and a source that is a URL becomes a link —
 * the whole point of the source is that a cataloguer can open the methodics it came from.
 *
 * The catalog and the register are fetched by the popover body, which mounts only when
 * opened — a form of sixty subfields does not pay for help nobody asked for. A user who
 * may not read the semantic catalog (a cataloguer may — it is in the cataloguing-form
 * capabilities) gets the lines it feeds left out rather than an error; a link is shown
 * only to whoever may open its target. Links open in a new tab, so the form stays as it is.
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStripes } from '@folio/stripes/core';
import HelpTooltip from './HelpTooltip';
import { useSemanticCatalog } from '../hooks/useSemanticCatalog';
import { useVocabularyTypes } from '../hooks/useVocabularyTypes';
import { RecordType, VOCABULARY_TYPE_CONFIG } from '../types/api';
import { rdaElementUrl, subfieldProperties, subfieldRepeatable, subfieldSource, } from './subfieldSource';
import { SETTINGS_CAPABILITY } from '../utils/settingsAccess';
var BASE_PATH = '/czna-authorities';
var MSG = 'ui-czna-authorities.subfieldSource';
/**
 * Permission each link's target needs; the link is UX, the server still answers 403.
 *
 * <p>`hasPerm` answers from the user's real permissions since CZNA-1425 dropped `hasAllPerms`.
 * Everyone who can open the proposal form holds both vocabulary reads — mod-czna-users'
 * CapabilitySync grants them with the cataloguing form (CZNA-1339) — so those links stay.
 * The ontology page is part of ⚙ Nastavení, so only the authority admin gets that link
 * (CZNA-1448); everyone else would land on the dashboard.
 */
export var LINK_PERMS = {
    browse: 'czna-authorities.vocabularies.collection.get',
    usage: 'czna-authorities.vocabularies.usage.get',
    catalog: SETTINGS_CAPABILITY,
};
/**
 * The popover's facts (repeatability, ontology properties) — a read every cataloguer holds
 * with the form, unlike the ontology PAGE behind {@link LINK_PERMS}.catalog.
 */
export var CATALOG_READ_PERM = 'czna-authorities.semantic-catalog.get';
var URL_PATTERN = /(https?:\/\/[^\s<>"]+[^\s<>".,;:!?)])/g;
/** Splits text into plain runs and `http(s)` links; everything else stays text. */
export var linkify = function (text) { return text
    .split(URL_PATTERN)
    .filter(function (part) { return part !== ''; })
    .map(function (part, index) { return (/^https?:\/\//.test(part)
    ? React.createElement("a", { key: index, href: part, target: "_blank", rel: "noopener noreferrer" }, part)
    : React.createElement(React.Fragment, { key: index }, part)); }); };
/** NK's description and its source (CZNA-1340). */
export var SubfieldHelpContent = function (_a) {
    var help = _a.help;
    return (React.createElement("div", { style: { whiteSpace: 'pre-line' } },
        React.createElement("div", null, help.description),
        help.source && (React.createElement("div", { style: { marginTop: '0.5rem', fontSize: '0.9em' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.subfieldDescription.sourceLabel" }),
            ' ',
            linkify(help.source)))));
};
var sectionStyle = { marginTop: '0.5rem' };
var linkListStyle = {
    display: 'flex', flexWrap: 'wrap', gap: '0.25rem 0.75rem', marginTop: '0.5rem',
};
/** A type the bundle does not know (configured at runtime) reads as its code. */
var authorityTypeName = function (intl, type) { return (Object.values(RecordType).includes(type)
    ? intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type) })
    : type); };
var NewTabLink = function (_a) {
    var href = _a.href, testId = _a.testId, children = _a.children;
    return (React.createElement("a", { href: href, target: "_blank", rel: "noopener noreferrer", "data-testid": testId }, children));
};
/** `ind1 = 1` · `ind1 = 0, 3`; alternatives joined by the locale's "or". */
var indicatorCondition = function (intl, whenIndicator) { return (intl.formatList(whenIndicator.map(function (when) { return Object.entries(when)
    .map(function (_a) {
    var ind = _a[0], values = _a[1];
    return "".concat(ind, " = ").concat(values.join(', '));
})
    .join(', '); }), { type: 'disjunction' })); };
var PropertyLine = function (_a) {
    var _b;
    var property = _a.property, testKey = _a.testKey;
    var intl = useIntl();
    var alignments = Object.entries(property.alignments);
    var label = intl.locale.startsWith('cs') ? property.label.cs : ((_b = property.label.en) !== null && _b !== void 0 ? _b : property.label.cs);
    return (React.createElement("div", { "data-testid": "subfield-source-".concat(testKey, "-property-").concat(property.name) },
        React.createElement(FormattedMessage, { id: "".concat(MSG, ".stewardship.").concat(property.stewardship), values: { property: label } }),
        property.whenIndicator.length > 0 && (React.createElement(React.Fragment, null,
            ' ',
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".whenIndicator"), values: { condition: indicatorCondition(intl, property.whenIndicator) } }))),
        alignments.map(function (_a) {
            var scheme = _a[0], targets = _a[1];
            return (
            // One group per vocabulary, every target listed (CZNA-434: COORDINATES aligns to
            // geo:lat AND geo:long). An RDA element is a link named "RDA …"; any other
            // vocabulary is named once before its targets, which keep their prefix.
            React.createElement("span", { key: scheme },
                ' · ',
                targets.every(function (target) { return rdaElementUrl(target); }) ? null : "".concat(scheme, " "),
                targets.map(function (target, i) {
                    var rda = rdaElementUrl(target);
                    return (React.createElement(React.Fragment, { key: target },
                        i > 0 && ', ',
                        rda
                            ? React.createElement(NewTabLink, { href: rda, testId: "subfield-source-".concat(testKey, "-rda-").concat(target) }, "RDA ".concat(target))
                            : target));
                })));
        })));
};
/** The derived facts about the subfield; mounts only inside the opened popover. */
export var SubfieldSourceContent = function (_a) {
    var _b;
    var tag = _a.tag, sf = _a.sf, label = _a.label, testKey = _a.testKey;
    var intl = useIntl();
    var stripes = useStripes();
    var source = subfieldSource(sf);
    var isVocabulary = source.kind === 'vocabulary';
    var canReadCatalog = stripes.hasPerm(CATALOG_READ_PERM);
    var catalog = useSemanticCatalog({ enabled: canReadCatalog }).data;
    var _c = useVocabularyTypes({ enabled: isVocabulary }), vocabularyTypes = _c.vocabularyTypes, registryLoading = _c.isLoading, registryFailed = _c.isError;
    var repeatable = catalog ? subfieldRepeatable(catalog, tag, sf.code) : undefined;
    var properties = catalog ? subfieldProperties(catalog, tag, sf.code) : [];
    var sourceLine;
    if (source.kind === 'vocabulary' || source.kind === 'authority') {
        var registry = vocabularyTypes.find(function (t) { return t.code === source.type; });
        // A CLOSED register refuses new terms whatever the template says — the picker reads it
        // the same way (`vocabularyIsClosed` in RecordPickerSubfield). Until the registry has
        // answered — or when it could not — "open" would be a guess, so the line leaves it out.
        var openness = isVocabulary && (registryLoading || registryFailed)
            ? null
            : source.allowFreeText && (registry === null || registry === void 0 ? void 0 : registry.validationType) !== 'CLOSED';
        var name_1 = source.kind === 'vocabulary'
            ? (VOCABULARY_TYPE_CONFIG[source.type]
                ? intl.formatMessage({ id: VOCABULARY_TYPE_CONFIG[source.type].labelKey })
                : ((_b = registry === null || registry === void 0 ? void 0 : registry.labelCs) !== null && _b !== void 0 ? _b : source.type))
            : authorityTypeName(intl, source.type);
        sourceLine = (React.createElement(React.Fragment, null,
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".source.").concat(source.kind), values: { name: name_1, code: source.type } }),
            openness !== null && (React.createElement(React.Fragment, null,
                ' · ',
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".").concat(openness ? 'open' : 'closed', ".").concat(source.kind) })))));
    }
    else if (source.kind === 'derived') {
        sourceLine = (React.createElement(FormattedMessage, { id: "".concat(MSG, ".source.derived"), values: { tag: source.tag, code: source.subfield } }));
    }
    else {
        sourceLine = React.createElement(FormattedMessage, { id: "".concat(MSG, ".source.").concat(source.kind) });
    }
    var links = [];
    if (isVocabulary && stripes.hasPerm(LINK_PERMS.browse)) {
        links.push(React.createElement(NewTabLink, { key: "browse", testId: "subfield-source-".concat(testKey, "-browse"), href: "".concat(BASE_PATH, "/vocabularies?type=").concat(encodeURIComponent(source.type)) },
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".link.browse") })));
    }
    if (isVocabulary && stripes.hasPerm(LINK_PERMS.usage)) {
        links.push(React.createElement(NewTabLink, { key: "usage", testId: "subfield-source-".concat(testKey, "-usage"), href: "".concat(BASE_PATH, "/vocabularies/usage/").concat(encodeURIComponent(source.type)) },
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".link.usage") })));
    }
    if (stripes.hasPerm(LINK_PERMS.catalog)) {
        links.push(React.createElement(NewTabLink, { key: "catalog", testId: "subfield-source-".concat(testKey, "-catalog"), href: "".concat(BASE_PATH, "/semantic-catalog/fields/").concat(encodeURIComponent(tag)) },
            React.createElement(FormattedMessage, { id: "".concat(MSG, ".link.catalog") })));
    }
    return (React.createElement("div", { "data-testid": "subfield-source-".concat(testKey) },
        React.createElement("div", null,
            React.createElement("strong", null, "".concat(tag, " $").concat(sf.code)),
            label && " \u2014 ".concat(label),
            repeatable !== undefined && (React.createElement(React.Fragment, null,
                ' · ',
                React.createElement(FormattedMessage, { id: "".concat(MSG, ".").concat(repeatable ? 'repeatable' : 'notRepeatable') })))),
        React.createElement("div", { style: sectionStyle, "data-testid": "subfield-source-".concat(testKey, "-source") }, sourceLine),
        catalog && (React.createElement("div", { style: sectionStyle }, properties.length > 0
            ? properties.map(function (p) { return React.createElement(PropertyLine, { key: p.name, property: p, testKey: testKey }); })
            : React.createElement(FormattedMessage, { id: "".concat(MSG, ".unbound") }))),
        links.length > 0 && React.createElement("div", { style: linkListStyle }, links)));
};
var SubfieldHelpButton = function (_a) {
    var tag = _a.tag, subfield = _a.subfield, label = _a.label, help = _a.help;
    var intl = useIntl();
    var testKey = "".concat(tag, "-").concat(subfield.code);
    return (React.createElement(HelpTooltip, { content: (React.createElement(React.Fragment, null,
            help && (React.createElement("div", { style: { marginBottom: '0.75rem' } },
                React.createElement(SubfieldHelpContent, { help: help }))),
            React.createElement(SubfieldSourceContent, { tag: tag, sf: subfield, label: label, testKey: testKey }))), testKey: testKey, idPrefix: "subfield-help", ariaLabel: intl.formatMessage({ id: "".concat(MSG, ".ariaLabel") }, { tag: tag, code: subfield.code }) }));
};
export default SubfieldHelpButton;
